/**
 * OAuthCallbackPage — the page Cognito Hosted-UI redirects back to after Google
 * authentication (route: ROUTES.OAUTH_CALLBACK, registered as a PUBLIC route in
 * App.tsx, outside AuthGuard, since the user is mid-authentication when this
 * mounts).
 *
 * Implements google-sign-in/US-001 (T6, AC-001.3, AC-001.5). Reconciles the
 * Amplify session (oauthReconciler.ts, T4), hydrates it into the existing
 * localStorage/Zustand token shape (useAuth.hydrateFederatedSession, T3), then
 * routes the parent to their landing page using the SAME getPostLoginRoute(groups)
 * fork used by password login (LoginPage.tsx) and RootRedirect.
 *
 * Edge cases handled with real, working behaviour (never a stub):
 *   - Cancelled at Google consent -> clean return to /login, no error banner, no
 *     partial session (AC-001.5).
 *   - No email from provider -> exact story-mandated message, tokens cleared.
 *   - Network failure during callback/token exchange -> retriable message + Retry.
 *   - Linking race (two near-simultaneous first logins) -> one automatic retry
 *     before surfacing a NETWORK error to the parent.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { getPostLoginRoute, ROUTES } from '@/constants/routes';
import {
  reconcileOAuthSession,
  fetchParentProfile,
  OAuthReconcileError,
} from '@/services/oauthReconciler';
import {
  readAndClearCapturedReferral,
  readAndClearOAuthIntent,
  readAndClearCapturedConsent,
} from '@/services/referralCapture';
import { applyReferral } from '@/services/referral';
import { recordConsent } from '@/services/consentApi';

type Status = 'reconciling' | 'error';
type ErrorKind = 'no_email' | 'network' | null;

const NO_EMAIL_MESSAGE =
  "We couldn't get your email from Google. Please try again or sign in with email and password.";
const NETWORK_MESSAGE = "Sign-in didn't complete. Please check your connection and try again.";
// BUG-20260713-020205 (D2, Option B) / T-9: shown to a not-yet-registered
// (consentPending) Google account that clicked "Continue with Google" on the
// LOGIN page — bounced to /register so registration (and referral capture)
// always flows through the signup door.
const NOT_REGISTERED_MESSAGE =
  "We couldn't find an existing account for that Google sign-in. Please sign up to create your account.";

// google-sign-in/US-001 (T6 step 5): absorb the "first-login linking race" edge
// case (two near-simultaneous first logins) with one automatic retry before
// surfacing an error to the parent.
const LINKING_RACE_RETRY_DELAY_MS = 800;

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const { hydrateFederatedSession, clearLocalSession } = useAuth();
  const [status, setStatus] = useState<Status>('reconciling');
  const [errorKind, setErrorKind] = useState<ErrorKind>(null);
  // React 18 StrictMode double-invokes effects in dev — guard so reconcile (and
  // its /users/me call) runs exactly once per mount (T6 performance_considerations).
  const hasRunRef = useRef(false);
  const hasRetriedRef = useRef(false);

  const runReconcile = useCallback(async () => {
    setStatus('reconciling');
    setErrorKind(null);
    try {
      const { tokens, userBase } = await reconcileOAuthSession();
      // Write tokens into the existing shape FIRST so the api.ts request
      // interceptor can attach the Bearer idToken for the /users/me call below.
      hydrateFederatedSession(tokens, { userId: userBase.userId, email: '' });

      const profile = await fetchParentProfile();

      // BUG-20260713-020205 (D2, Option B) / T-9: always read-and-clear the
      // captured OAuth intent (belt-and-braces, exactly like the referral
      // read-and-clear below) so a stale marker never lingers for a later
      // sign-in. A not-yet-registered (consentPending) Google account that
      // clicked "Continue with Google" on the LOGIN page is bounced to
      // /register — registration (and referral capture) must flow
      // exclusively through the signup door (Option B). A missing/unreadable
      // intent (e.g. sessionStorage cleared/private mode) fails safe: it is
      // NOT 'login', so no bounce happens here — the D1 consent gate
      // (consentPending + Cognito 'parent' group membership) still blocks
      // real feature access regardless, so no compliance gap opens.
      const intent = readAndClearOAuthIntent();
      if (intent === 'login' && profile.consentPending === true) {
        clearLocalSession();
        navigate(ROUTES.REGISTER, {
          replace: true,
          state: { message: NOT_REGISTERED_MESSAGE },
        });
        return;
      }

      // Re-hydrate with the real email/name/channel now that we have them, and use
      // the RETURNED federatedUser (groups decoded from the idToken's
      // cognito:groups claim) for routing — /users/me's response does not carry
      // groups (AC-001.3).
      const federatedUser = hydrateFederatedSession(tokens, profile);

      // google-sign-in/US-005 (T5) / AC-005.2, AC-005.4, BR-005.1: submit any
      // referral code captured pre-redirect (T3/T4) NOW that provisioning
      // (the fetchParentProfile /users/me call above, US-002) has completed
      // and the referee's authoritative Users record exists. Always read-
      // and-clear (even when null) so a stale key never lingers for a later
      // manual sign-in (AC-005.3 belt-and-braces). Strictly best-effort: a
      // failed/invalid/self referral is invisible to the new parent and must
      // NEVER block or delay routing into the app.
      const capturedReferralCode = readAndClearCapturedReferral();
      if (capturedReferralCode) {
        try {
          await applyReferral(capturedReferralCode);
        } catch (err) {
          console.warn('[OAuthCallbackPage] referral apply failed (non-blocking)', err);
        }
      }

      // consent-on-google-signup: if the parent ticked the T&C/Privacy box on
      // the RegisterPage before the Google redirect (captured pre-redirect,
      // exactly like the referral code), write the consent record NOW that
      // provisioning has completed and the parent's authoritative record exists.
      // This collapses the post-login consent modal into the register-page
      // checkbox for the Google signup door. Always read-and-clear (even when
      // absent) so a stale marker never lingers for a later sign-in. Only write
      // when the fresh parent is still consentPending — never re-stamp an
      // already-consented user. Strictly best-effort: a failed write simply
      // leaves the server-side require_consent() gate to surface the modal as
      // the fail-closed fallback, and must NEVER block or delay routing.
      const capturedConsent = readAndClearCapturedConsent();
      if (capturedConsent && profile.consentPending === true) {
        try {
          await recordConsent();
        } catch (err) {
          console.warn('[OAuthCallbackPage] consent record failed (non-blocking)', err);
        }
      }

      navigate(getPostLoginRoute(federatedUser.groups), { replace: true });
    } catch (err) {
      const code = err instanceof OAuthReconcileError ? err.code : 'NETWORK';

      if (code === 'NO_SESSION') {
        // AC-001.5: cancelled flow -> clean return to /login, NO error banner, no
        // partial session. Local-only clear (never a second Hosted-UI redirect —
        // there is nothing to remotely sign out of when the user simply cancelled
        // at Google's consent screen).
        clearLocalSession();
        navigate(ROUTES.LOGIN, { replace: true });
        return;
      }

      if (code === 'NO_EMAIL') {
        clearLocalSession();
        setErrorKind('no_email');
        setStatus('error');
        return;
      }

      // NETWORK — first failure gets ONE automatic retry (linking-race edge case)
      // before surfacing a retriable error to the parent.
      if (!hasRetriedRef.current) {
        hasRetriedRef.current = true;
        clearLocalSession(); // clear any partial tokens before retrying
        setTimeout(() => {
          void runReconcile();
        }, LINKING_RACE_RETRY_DELAY_MS);
        return;
      }

      clearLocalSession();
      setErrorKind('network');
      setStatus('error');
    }
  }, [hydrateFederatedSession, clearLocalSession, navigate]);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    void runReconcile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="text-center max-w-sm space-y-4">
          <p className="text-sm text-red-600">
            {errorKind === 'no_email' ? NO_EMAIL_MESSAGE : NETWORK_MESSAGE}
          </p>
          <div className="flex flex-col gap-2">
            {errorKind === 'network' && (
              <Button
                variant="warm"
                className="rounded-[10px]"
                onClick={() => {
                  hasRetriedRef.current = false;
                  void runReconcile();
                }}
              >
                Retry
              </Button>
            )}
            <Button
              variant="outline"
              className="rounded-[10px]"
              onClick={() => navigate(ROUTES.LOGIN, { replace: true })}
            >
              Back to sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
        <p className="mt-2 text-sm text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
}
